import React from "react";
import { Edit, Package, Tag, BoxesIcon } from "lucide-react";
import { infoToast } from "../../../../../services/toast.service";

const ProductSellAs = ({ sellAsOptions }) => {
  // If no sellAsOptions are provided, use sample data
  const sellAsData = sellAsOptions || {
    primaryUnit: "Pairs",
    // This array represents which configurations were actually selected by the user
    selectedConfigurations: ["secondary", "tertiary"],
    configurations: {
      // Even though we have data for primary, if it's not in selectedConfigurations, we won't show it
      primary: {
        name: "Individual Pair",
        price: 499,
      },
      secondary: {
        name: "Standard Pack",
        description: "Regular shoe pack with mixed sizes",
        price: 3992,
        items: 8,
      },
      tertiary: {
        name: "Carton Pack",
        description: "Bulk packaging for wholesale distribution",
        price: 22956,
        items: 48,
      },
    },
  };

  // Get only the configurations that were selected by the user
  const getVisibleConfigurations = () => {
    if (
      !sellAsData.selectedConfigurations ||
      sellAsData.selectedConfigurations.length === 0
    ) {
      return [];
    }

    return sellAsData.selectedConfigurations.map(configType => {
      const config = sellAsData.configurations[configType];
      return {
        ...config,
        type: configType,
        // Create dummy items if real ones don't exist
        items:
          config.items && Array.isArray(config.items)
            ? config.items
            : [
                {
                  color: "Default",
                  size: config.name,
                  price: config.price || 0,
                  quantity: config.items || 1,
                },
              ],
        totalItems: config.items || 1,
        totalValue: config.price || 0,
      };
    });
  };

  const visibleConfigurations = getVisibleConfigurations();

  // Icon mapping for different pack types
  const getPackIcon = type => {
    switch (type) {
      case "primary":
        return <Tag size={16} className="text-green-600" />;
      case "secondary":
        return <Package size={16} className="text-[#5652B0]" />;
      case "tertiary":
        return <BoxesIcon size={16} className="text-amber-600" />;
      default:
        return <Package size={16} className="text-[#5652B0]" />;
    }
  };

  // Get color for pack type
  const getPackTypeColor = type => {
    switch (type) {
      case "primary":
        return "bg-green-50 text-green-700 border-green-200";
      case "secondary":
        return "bg-[#F9F8FE] text-[#5652B0] border-[#EFEDFB]";
      case "tertiary":
        return "bg-amber-50 text-amber-700 border-amber-200";
      default:
        return "bg-gray-50 text-gray-700 border-gray-200";
    }
  };

  // Get badge text
  const getPackTypeLabel = type => {
    switch (type) {
      case "primary":
        return "Primary";
      case "secondary":
        return "Secondary";
      case "tertiary":
        return "Tertiary";
      default:
        return type;
    }
  };

  // Helper function to format the description of how this product can be sold
  const getConfigurationDescription = () => {
    if (
      !sellAsData.selectedConfigurations ||
      sellAsData.selectedConfigurations.length === 0
    ) {
      return `This product is not configured for selling in packs.`;
    }

    const configLabels = sellAsData.selectedConfigurations.map(config => {
      switch (config) {
        case "primary":
          return "individual units";
        case "secondary":
          return "secondary packs";
        case "tertiary":
          return "tertiary packs";
        default:
          return config;
      }
    });

    if (configLabels.length === 1) {
      return `This product is sold as ${configLabels[0]}.`;
    }

    const lastConfig = configLabels.pop();
    return `This product is sold as ${configLabels.join(
      ", ",
    )} and ${lastConfig}.`;
  };

  return (
    <div className="bg-white rounded-lg p-4 border border-gray-200">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-base font-semibold text-gray-800">
          Sell As Configuration
        </h2>
        <button
          className="text-[#5652B0] hover:bg-[#F9F8FE] p-1.5 rounded-md"
          onClick={() => infoToast("Edit not available in demo.")}>
          <Edit size={16} />
        </button>
      </div>

      <div className="text-sm text-gray-500 mb-4">
        {getConfigurationDescription()}
      </div>

      <div className="space-y-4">
        {visibleConfigurations.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            No selling configurations have been set up for this product.
          </div>
        ) : (
          visibleConfigurations.map((config, index) => (
            <div
              key={index}
              className="bg-[#F9FAFB] border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-white p-3 border-b border-gray-200 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  {getPackIcon(config.type)}
                  <span className="font-medium text-gray-800">
                    {config.name}
                  </span>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full ${getPackTypeColor(
                      config.type,
                    )}`}>
                    {getPackTypeLabel(config.type)}
                  </span>
                </div>
                {config.totalItems && (
                  <div className="text-xs text-gray-500">
                    {config.totalItems} items · ₹
                    {config.totalValue.toLocaleString()}
                  </div>
                )}
              </div>

              <div className="p-3">
                {config.description && (
                  <p className="text-xs text-gray-500 mb-3">
                    {config.description}
                  </p>
                )}

                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse text-xs">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="py-2 px-3 text-left font-medium text-gray-600 border-b border-gray-200">
                          Unit Type
                        </th>
                        <th className="py-2 px-3 text-center font-medium text-gray-600 border-b border-gray-200">
                          Details
                        </th>
                        <th className="py-2 px-3 text-right font-medium text-gray-600 border-b border-gray-200">
                          Price (₹)
                        </th>
                        <th className="py-2 px-3 text-right font-medium text-gray-600 border-b border-gray-200">
                          Qty
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(config.items) ? (
                        config.items.map((item, itemIndex) => (
                          <tr key={itemIndex} className="hover:bg-gray-50">
                            <td className="py-2 px-3 border-b border-gray-100">
                              {item.color || "Standard"}
                            </td>
                            <td className="py-2 px-3 text-center border-b border-gray-100">
                              {item.size || "Regular"}
                            </td>
                            <td className="py-2 px-3 text-right border-b border-gray-100">
                              {item.price ? item.price.toLocaleString() : 0}
                            </td>
                            <td className="py-2 px-3 text-right border-b border-gray-100">
                              {item.quantity || 1}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr className="hover:bg-gray-50">
                          <td className="py-2 px-3 border-b border-gray-100">
                            {config.name}
                          </td>
                          <td className="py-2 px-3 text-center border-b border-gray-100">
                            {config.description || "-"}
                          </td>
                          <td className="py-2 px-3 text-right border-b border-gray-100">
                            {config.price ? config.price.toLocaleString() : 0}
                          </td>
                          <td className="py-2 px-3 text-right border-b border-gray-100">
                            {config.items || 1}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ProductSellAs;
